import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useCatalog } from "../../catalog/CatalogContext";
import { useCart } from "../../cart/CartContext";
import { CartItem } from "../../cart/cartTypes";
import { formatPrice, getCartTotal } from "../../cart/cartUtils";
import { Product, buildProductSlug } from "../../data/products";
import CartCheckoutModal from "./CartCheckoutModal";

type CartDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

function CartRow({ item, product }: { item: CartItem; product: Product }) {
  const { updateQuantity, removeItem } = useCart();
  const maxQty = product.stock ?? 99;

  return (
    <div className="rounded-[24px] border border-[rgba(62,35,24,0.08)] bg-white/70 p-4">
      <div className="flex gap-4">
        <Link
          to={`/product/${buildProductSlug(product)}`}
          className="flex h-24 w-24 items-center justify-center rounded-[20px] bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.94),rgba(239,223,208,0.92))] p-3"
        >
          <img
            src={product.image}
            alt={product.title}
            className="max-h-full max-w-full object-contain"
          />
        </Link>

        <div className="flex-1">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="brand-display text-xl font-bold text-[#221510]">
                {product.title}
              </p>
              {item.size && (
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-[#8b6d5d]">
                  Pointure {item.size}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => removeItem(item)}
              className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8b6d5d]"
            >
              Retirer
            </button>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="inline-flex items-center rounded-full border border-[rgba(62,35,24,0.1)] bg-[#fff9f3] p-1">
              <button
                type="button"
                onClick={() => updateQuantity(item, item.quantity - 1)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-lg text-[#2b1b15]"
              >
                −
              </button>
              <span className="min-w-[2.25rem] text-center text-sm font-semibold text-[#201410]">
                {item.quantity}
              </span>
              <button
                type="button"
                onClick={() => updateQuantity(item, item.quantity + 1)}
                disabled={item.quantity >= maxQty}
                className="flex h-9 w-9 items-center justify-center rounded-full text-lg text-[#2b1b15] disabled:opacity-35"
              >
                +
              </button>
            </div>

            <span className="text-sm font-semibold text-[#201410]">
              {formatPrice(product.priceValue * item.quantity)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { products } = useCatalog();
  const { items, clear } = useCart();
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  const total = useMemo(() => getCartTotal(items, products), [items, products]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-[rgba(20,10,5,0.52)] backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <aside className="fixed right-3 top-3 z-[65] flex h-[calc(100%-1.5rem)] w-[calc(100%-1.5rem)] max-w-md flex-col overflow-hidden rounded-[34px] border border-[rgba(62,35,24,0.1)] bg-[linear-gradient(180deg,rgba(255,249,241,0.97),rgba(244,233,222,0.96))] shadow-[0_30px_70px_rgba(38,20,13,0.25)]">
        <div className="border-b border-[rgba(62,35,24,0.08)] px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="brand-eyebrow">Panier</p>
              <h3 className="brand-display mt-2 text-3xl font-bold text-[#21140f]">
                Vos envies
              </h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(62,35,24,0.1)] bg-white/70 text-xl text-[#2c1b15]"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-auto px-6 py-6">
          {items.length === 0 && (
            <div className="rounded-[24px] border border-dashed border-[rgba(62,35,24,0.16)] bg-white/50 px-5 py-12 text-center">
              <p className="brand-display text-3xl font-bold text-[#21140f]">
                Votre panier est vide
              </p>
              <p className="mt-3 text-sm leading-7 text-[#755f53]">
                Ajoutez vos pieces preferees pour continuer vos achats.
              </p>
            </div>
          )}

          {items.map((item) => {
            const product = products.find((entry) => entry.id === item.productId);
            if (!product) return null;

            return (
              <CartRow
                key={`${item.productId}-${item.size ?? "na"}`}
                item={item}
                product={product}
              />
            );
          })}
        </div>

        <div className="border-t border-[rgba(62,35,24,0.08)] bg-white/62 px-6 py-6">
          <div className="flex items-center justify-between rounded-[24px] bg-[#241712] px-5 py-4 text-[#fff1e5]">
            <span className="text-[11px] font-semibold uppercase tracking-[0.28em]">
              Total
            </span>
            <span className="brand-display text-2xl font-bold">
              {formatPrice(total)}
            </span>
          </div>

          <div className="mt-4 grid gap-3">
            <button
              type="button"
              onClick={() => setIsCheckoutOpen(true)}
              disabled={items.length === 0}
              className="cta-primary rounded-full px-6 py-4 text-xs font-semibold uppercase tracking-[0.3em] disabled:opacity-40"
            >
              Passer commande
            </button>

            <button
              type="button"
              onClick={clear}
              disabled={items.length === 0}
              className="cta-secondary rounded-full px-6 py-4 text-xs font-semibold uppercase tracking-[0.3em] disabled:opacity-40"
            >
              Vider le panier
            </button>
          </div>
        </div>
      </aside>

      <CartCheckoutModal
        isOpen={isCheckoutOpen}
        items={items}
        onClose={() => setIsCheckoutOpen(false)}
        onComplete={() => {
          setIsCheckoutOpen(false);
          clear();
          onClose();
        }}
      />
    </>
  );
}
